// FILE: CollegeStudentModals.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Save, AlertCircle, CheckCircle, Key, Archive, RotateCcw,
  Mail, Eye, EyeOff, ShieldCheck, BookOpen, Award, BarChart2,
  CreditCard
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

// -- Shared overlay + modal shell ---------------------------------------------
const ModalOverlay = ({ onClose, children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15,23,42,0.55)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 16,
    }}
  >
    <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480 }}>
      {children}
    </div>
  </div>
);

const ModalCard = ({ accentColor, children }) => (
  <div style={{
    background: '#FFFFFF',
    borderRadius: 20,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    border: '1px solid var(--border-subtle, #E2E8F0)',
    overflow: 'hidden',
    maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
  }}>
    <div style={{
      height: 4,
      background: accentColor || 'linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)',
      flexShrink: 0,
    }} />
    {children}
  </div>
);

const ModalHeader = ({ title, icon, onClose }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 24px 16px',
    borderBottom: '1px solid var(--border-subtle, #E2E8F0)',
    flexShrink: 0,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {icon}
      <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0F172A', margin: 0, letterSpacing: '-0.01em' }}>
        {title}
      </h2>
    </div>
    <button
      onClick={onClose}
      style={{
        width: 34, height: 34, borderRadius: 10,
        border: '1px solid var(--border-subtle, #E2E8F0)',
        background: 'transparent', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#94A3B8', transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#0F172A'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}
    >
      <X size={17} />
    </button>
  </div>
);

const FieldLabel = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
    {children}{required && <span style={{ color: '#DC2626', marginLeft: 3 }}>*</span>}
  </label>
);

const inputBase = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--border-subtle, #E2E8F0)',
  borderRadius: 10,
  fontSize: '0.875rem',
  color: '#0F172A',
  background: '#FFFFFF',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border 0.15s, box-shadow 0.15s',
};

const focusInput = (e) => {
  e.target.style.border = '1px solid var(--primary-blue, #4F46E5)';
  e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)';
};
const blurInput = (e) => {
  e.target.style.border = '1px solid var(--border-subtle, #E2E8F0)';
  e.target.style.boxShadow = 'none';
};

const Alert = ({ type, children }) => {
  const cfg = type === 'success'
    ? { bg: '#DCFCE7', border: '1px solid #BBF7D0', color: '#166534', icon: <CheckCircle size={16} style={{ color: '#16A34A', flexShrink: 0 }} /> }
    : { bg: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', icon: <AlertCircle size={16} style={{ color: '#DC2626', flexShrink: 0 }} /> };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: cfg.bg, border: cfg.border, borderRadius: 10 }}>
      {cfg.icon}
      <p style={{ margin: 0, fontSize: '0.875rem', color: cfg.color, lineHeight: 1.5 }}>{children}</p>
    </div>
  );
};

const ActionButton = ({ onClick, disabled, loading, bg, hoverBg, icon, label, type = 'button' }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '10px 18px',
        borderRadius: 10, border: 'none',
        background: disabled || loading ? '#CBD5E1' : (hovered ? hoverBg : bg),
        color: '#FFFFFF',
        fontSize: '0.875rem', fontWeight: 700,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {loading ? (
        <>
          <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />
          Processing...
        </>
      ) : (
        <>{icon} {label}</>
      )}
    </button>
  );
};

const CancelButton = ({ onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      flex: 1, padding: '10px 18px',
      border: '1px solid var(--border-subtle, #E2E8F0)',
      borderRadius: 10, background: '#FFFFFF',
      fontSize: '0.875rem', fontWeight: 600, color: '#374151',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#F8FAFC'; }}
    onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; }}
  >
    Cancel
  </button>
);

// -- Bullet-point info row ----------------------------------------------------
const InfoRow = ({ icon, text, strike = false }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0' }}>
    <span style={{ color: '#64748B', flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <span style={{
      fontSize: '0.875rem', color: '#374151', lineHeight: 1.5,
      textDecoration: strike ? 'line-through' : 'none',
      opacity: strike ? 0.5 : 1,
    }}>{text}</span>
  </div>
);

// -----------------------------------------------------------------------------
// Edit Student Modal
// -----------------------------------------------------------------------------
export const EditStudentModal = ({ student, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', roll_number: '', branch: '', semester: '', status: 'ACTIVE',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (student) {
      setFormData({
        student_id:  student.student_id  || '',
        name:        student.name        || '',
        email:       student.email       || '',
        roll_number: student.roll_number || '',
        branch:      student.branch      || '',
        semester:    student.semester    || '',
        status:      student.status      || 'ACTIVE',
      });
    }
  }, [student]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null); setSuccess(false);
    try {
      const { student_id, ...dataToUpdate } = formData;
      await axios.put(`${API_BASE_URL}/students/update`, { student_id: student.student_id, ...dataToUpdate });
      setSuccess(true);
      setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 1500);
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.message || 'Failed to update student');
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (e) => setFormData(prev => ({ ...prev, [key]: e.target.value }));

  const fields = [
    { key: 'student_id',  label: 'Student ID',    type: 'text',  readOnly: true },
    { key: 'name',        label: 'Full Name',      type: 'text',  required: true },
    { key: 'email',       label: 'Email Address',  type: 'email', required: true },
    { key: 'roll_number', label: 'Roll Number',    type: 'text',  required: true },
    { key: 'branch',      label: 'Branch',         type: 'text',  required: false },
    { key: 'semester',    label: 'Semester',       type: 'text',  required: false },
  ];

  return (
    <ModalOverlay onClose={onClose}>
      <ModalCard accentColor="linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)">
        <ModalHeader
          title="Edit Student"
          icon={<div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(79, 70, 229,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Save size={17} style={{ color: 'var(--primary-blue, #4F46E5)' }} /></div>}
          onClose={onClose}
        />

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#F8FAFC', border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(79, 70, 229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--primary-blue, #4F46E5)' }}>{(student?.name || '?')[0].toUpperCase()}</span>
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' }}>{student?.name}</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748B' }}>
                Roll: {student?.roll_number || 'N/A'} • ID: {student?.student_id}
              </p>
            </div>
          </div>

          {fields.map(f => (
            <div key={f.key}>
              <FieldLabel required={f.required}>{f.label}</FieldLabel>
              <input
                type={f.type}
                value={formData[f.key]}
                onChange={f.readOnly ? undefined : set(f.key)}
                required={f.required}
                readOnly={f.readOnly}
                style={{
                  ...inputBase,
                  backgroundColor: f.readOnly ? '#F1F5F9' : '#FFFFFF',
                  cursor: f.readOnly ? 'not-allowed' : 'text',
                  color: f.readOnly ? '#64748B' : '#0F172A',
                }}
                onFocus={f.readOnly ? undefined : focusInput}
                onBlur={f.readOnly ? undefined : blurInput}
              />
            </div>
          ))}

          <div>
            <FieldLabel required>Status</FieldLabel>
            <select
              value={formData.status}
              onChange={set('status')}
              required
              style={{ ...inputBase, appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}
              onFocus={focusInput}
              onBlur={blurInput}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>

          {error   && <Alert type="error">{error}</Alert>}
          {success && <Alert type="success">Student updated successfully!</Alert>}

          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <CancelButton onClick={onClose} disabled={loading} />
            <ActionButton
              type="submit"
              loading={loading}
              disabled={success}
              bg="var(--primary-blue, #4F46E5)" hoverBg="var(--secondary-blue, #1E3A8A)"
              icon={<Save size={15} />}
              label="Save Changes"
            />
          </div>
        </form>
      </ModalCard>
    </ModalOverlay>
  );
};

// -----------------------------------------------------------------------------
// Reset Password Modal
// -----------------------------------------------------------------------------
export const ResetPasswordModal = ({ student, onClose, onSuccess }) => {
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied]         = useState(false);

  const handleReset = async () => {
    setLoading(true); setError(null); setSuccess(false);
    try {
      const response = await axios.post(`${API_BASE_URL}/students/reset-password`, {
        student_id: student.student_id,
        reset_by: localStorage.getItem('collegeUsername') || 'admin',
      });
      setNewPassword(response.data.new_password);
      setSuccess(true);
      setTimeout(() => { onSuccess && onSuccess(); }, 3000);
    } catch (err) {
      console.error('Reset error:', err);
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalCard accentColor="linear-gradient(90deg, #D97706, #EA580C)">
        <ModalHeader
          title="Reset Password"
          icon={<div style={{ width: 34, height: 34, borderRadius: 10, background: '#FFFBEB', border: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Key size={17} style={{ color: '#D97706' }} /></div>}
          onClose={onClose}
        />

        <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#F8FAFC', border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FFFBEB', border: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: '#D97706' }}>{(student?.name || '?')[0].toUpperCase()}</span>
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' }}>{student?.name}</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748B' }}>
                Roll: {student?.roll_number || 'N/A'} • ID: {student?.student_id}
              </p>
            </div>
          </div>

          {!success && (
            <div style={{ padding: '14px 16px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12 }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400E', lineHeight: 1.6 }}>
                A new random password will be generated and sent to the student's email. They must change it on their first login.
              </p>
            </div>
          )}

          {newPassword && (
            <div style={{ padding: '16px', background: '#DCFCE7', border: '1px solid #BBF7D0', borderRadius: 12 }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.8rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.06em' }}>New Password</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, padding: '9px 12px', background: '#FFFFFF', border: '1px solid #BBF7D0', borderRadius: 10, fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: '#0F172A', letterSpacing: '0.08em' }}>
                  {showPassword ? newPassword : '•'.repeat(newPassword.length)}
                </div>
                <button
                  onClick={() => setShowPassword(v => !v)}
                  style={{ padding: 10, borderRadius: 10, border: '1px solid #BBF7D0', background: '#FFFFFF', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#64748B' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button
                  onClick={handleCopy}
                  style={{ padding: '9px 16px', borderRadius: 10, border: 'none', background: copied ? '#16A34A' : '#D97706', color: '#FFFFFF', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', transition: 'background 0.2s' }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
                <Mail size={13} style={{ color: '#16A34A' }} />
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#166534' }}>Password has been sent to the student's email</p>
              </div>
            </div>
          )}

          {error && <Alert type="error">{error}</Alert>}

          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            {!success ? (
              <>
                <CancelButton onClick={onClose} disabled={loading} />
                <ActionButton
                  onClick={handleReset}
                  loading={loading}
                  bg="#D97706" hoverBg="#B45309"
                  icon={<Key size={15} />}
                  label="Reset Password"
                />
              </>
            ) : (
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '10px 18px',
                  borderRadius: 10, border: 'none',
                  background: 'var(--primary-blue, #4F46E5)', color: '#FFFFFF',
                  fontSize: '0.875rem', fontWeight: 700,
                  cursor: 'pointer', transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </ModalCard>
    </ModalOverlay>
  );
};

// -----------------------------------------------------------------------------
// Archive (Delete) Student Modal
// "DeleteStudentModal" export name preserved for backward compatibility.
// -----------------------------------------------------------------------------
export const DeleteStudentModal = ({ student, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleArchive = async () => {
    setLoading(true); setError(null);
    try {
      const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return {}; } })();
      await axios.delete(`${API_BASE_URL}/students/delete`, {
        data: {
          student_id:  student.student_id,
          deleted_by:  user?.username || user?.id || 'admin',
        },
      });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Archive error:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to archive student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalCard accentColor="linear-gradient(90deg, #DC2626, #EA580C)">
        <ModalHeader
          title="Archive Student"
          icon={<div style={{ width: 34, height: 34, borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Archive size={17} style={{ color: '#DC2626' }} /></div>}
          onClose={onClose}
        />

        <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Student info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#F8FAFC', border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: '#DC2626' }}>{(student?.name || '?')[0].toUpperCase()}</span>
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' }}>{student?.name}</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748B' }}>
                Roll: <strong style={{ color: '#374151' }}>{student?.roll_number || 'N/A'}</strong> • {student?.email}
              </p>
            </div>
          </div>

          {/* What this action does */}
          <div style={{ padding: '16px', background: '#FFF7F7', border: '1px solid #FECACA', borderRadius: 12 }}>
            <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#991B1B', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              This action will:
            </p>
            <InfoRow icon={<Archive size={15} />}   text="Remove the student from Student Directory" />
            <InfoRow icon={<Award size={15} />}     text="Preserve certificates and learning history" />
            <InfoRow icon={<BookOpen size={15} />}  text="Preserve bootcamp progress and assessments" />
            <InfoRow icon={<BarChart2 size={15} />} text="Preserve analytics and ranking data" />
            <InfoRow icon={<CreditCard size={15} />} text="Return one purchased student credit to your account" />
            <InfoRow icon={<RotateCcw size={15} />} text="Allow future restoration at any time" />
          </div>

          {/* Safe note */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10 }}>
            <ShieldCheck size={15} style={{ color: '#D97706', flexShrink: 0, marginTop: 2 }} />
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400E', lineHeight: 1.5 }}>
              This action will <strong>NOT</strong> permanently erase data. You can restore this student at any time.
            </p>
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <CancelButton onClick={onClose} disabled={loading} />
            <ActionButton
              onClick={handleArchive}
              loading={loading}
              bg="#DC2626" hoverBg="#B91C1C"
              icon={<Archive size={15} />}
              label="Archive Student"
            />
          </div>
        </div>
      </ModalCard>
    </ModalOverlay>
  );
};

// -----------------------------------------------------------------------------
// Restore Student Modal
// -----------------------------------------------------------------------------
export const RestoreStudentModal = ({ student, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleRestore = async () => {
    setLoading(true); setError(null);
    try {
      const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return {}; } })();
      await axios.post(`${API_BASE_URL}/students/restore`, {
        student_id:  student.student_id,
        restored_by: user?.username || user?.id || 'admin',
      });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Restore error:', err);
      const msg = err.response?.data?.error === 'INSUFFICIENT_CREDITS'
        ? 'Insufficient credits to restore this student. Please purchase more credits.'
        : err.response?.data?.error || err.response?.data?.message || 'Failed to restore student. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalCard accentColor="linear-gradient(90deg, #16A34A, #059669)">
        <ModalHeader
          title="Restore Student"
          icon={<div style={{ width: 34, height: 34, borderRadius: 10, background: '#DCFCE7', border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RotateCcw size={17} style={{ color: '#16A34A' }} /></div>}
          onClose={onClose}
        />

        <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Student info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#F8FAFC', border: '1px solid var(--border-subtle, #E2E8F0)', borderRadius: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DCFCE7', border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: '#16A34A' }}>{(student?.name || '?')[0].toUpperCase()}</span>
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' }}>{student?.name}</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748B' }}>
                Roll: <strong style={{ color: '#374151' }}>{student?.roll_number || 'N/A'}</strong> • {student?.email}
              </p>
            </div>
          </div>

          {/* What restore does */}
          <div style={{ padding: '16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12 }}>
            <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#166534', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Restoring this student will:
            </p>
            <InfoRow icon={<ShieldCheck size={15} />} text="Make the student active again immediately" />
            <InfoRow icon={<CreditCard size={15} />}  text="Consume one student credit from your account" />
            <InfoRow icon={<BarChart2 size={15} />}   text="Restore full dashboard and analytics visibility" />
            <InfoRow icon={<Award size={15} />}       text="Preserve all historical records and certificates" />
          </div>

          {/* Credit note */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10 }}>
            <CreditCard size={15} style={{ color: '#D97706', flexShrink: 0, marginTop: 2 }} />
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400E', lineHeight: 1.5 }}>
              <strong>1 credit will be deducted</strong> from your account upon restoration.
            </p>
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <CancelButton onClick={onClose} disabled={loading} />
            <ActionButton
              onClick={handleRestore}
              loading={loading}
              bg="#16A34A" hoverBg="#15803D"
              icon={<RotateCcw size={15} />}
              label="Restore Student"
            />
          </div>
        </div>
      </ModalCard>
    </ModalOverlay>
  );
};
